import { theme } from '@Guidelines/Theme/theme';
import { css } from '@emotion/react';
import useActiveNavigation from '@UI/Guidelines/Shell/AppShell/Hooks/useActiveNavigation';
import { RootBreadCrumb } from '@verclam/breadcrumbs';
import type * as React from 'react';

/**
 * The library emits its separator as a PrimeIcons glyph, and no icon font is installed: the chevron
 * is drawn by CSS instead. Its inline colors and the home link's `color: inherit` are overridden with
 * `!important`, the only way past inline styles.
 *
 * Under the compact breakpoint the section prefix is hidden and, when the trail holds at least two
 * elements, only the parent stays visible — the third child from the end of the list
 * (`[..., parent, separator, current]`) — prefixed with a back chevron.
 */
const containerStyle = css`
	display: flex;
	align-items: center;
	margin-bottom: 0.25rem;
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.medium};
	color: ${theme.colors.ink};

	a {
		color: ${theme.colors.accent} !important;
		text-decoration: none;
	}

	nav {
		padding: 0 !important;
		flex: 1;
	}

	ol > li:last-child > span {
		color: ${theme.colors.ink} !important;
		font-weight: ${theme.fontWeights.semibold};
	}

	li[aria-hidden='true'] {
		color: ${theme.colors.inkFaint} !important;
	}

	li[aria-hidden='true'] i::before {
		content: '›';
		font-style: normal;
	}

	// @media (max-width: ${theme.layout.compactBreakpoint}) {
	// 	ol:has(> li:nth-child(3)) > li:not(:nth-last-child(3)) {
	// 		display: none !important;
	// 	}
	//
	// 	ol > li:nth-last-child(3)::before {
	// 		content: '‹';
	// 		margin-right: 0.25rem;
	// 		color: ${theme.colors.accent};
	// 	}
	// }
`;

const sectionStyle = css`
	display: inline-flex;
	align-items: center;
	color: ${theme.colors.inkMuted};

	&::after {
		content: '›';
		margin: 0 0.5rem;
		color: ${theme.colors.inkFaint};
	}

	@media (max-width: ${theme.layout.compactBreakpoint}) {
		display: none;
	}
`;

/**
 * Renders the trail declared by the mounted `HomeBreadcrumb` / `ItemBreadcrumb` elements, prefixed
 * with the active section. Draws nothing — not even a margin — without an active navigation entry.
 */
const AppBreadcrumb: React.FC = () => {
	const active = useActiveNavigation();

	if (active === null) {
		return null;
	}

	return (
		<div css={containerStyle}>
			<span css={sectionStyle}>{active.section.label}</span>
			<RootBreadCrumb />
		</div>
	);
};

export default AppBreadcrumb;
